import { atom, type getDefaultStore } from 'jotai';
import type { Listing } from 'app/types/listing';

type Store = ReturnType<typeof getDefaultStore>;
export interface GroupedRequestState {
  phase: 'queued' | 'sending' | 'live' | 'error' | 'cancelled';
  deadline?: number;
  request?: Listing;
  error?: Error;
}
// Pending work is session-local. Published requests are rediscovered from the API.
export const groupedRequestsAtom = atom<Record<string, GroupedRequestState>>({});
export const UNDO_DURATION_MS = 10_000;

export function setGroupedRequest(store: Store, key: string, state: GroupedRequestState) {
  store.set(groupedRequestsAtom, (previous) => ({ ...previous, [key]: state }));
}

export function undoQueuedRequest(store: Store, key: string) {
  if (store.get(groupedRequestsAtom)[key]?.phase !== 'queued') return;
  setGroupedRequest(store, key, { phase: 'cancelled' });
}

// The queue lives beyond the screen: navigating away does not defeat Undo or
// interrupt sending. Nothing has been posted during the grace period.
// A terminated app drops unsent work; we never claim it has been published.
export function queueGroupedRequest(store: Store, key: string, send: () => Promise<Listing>, canSend: () => boolean) {
  const previous = store.get(groupedRequestsAtom)[key];
  if (previous && ['queued', 'sending', 'live'].includes(previous.phase)) return;
  const deadline = Date.now() + UNDO_DURATION_MS;
  setGroupedRequest(store, key, { phase: 'queued', deadline });
  setTimeout(() => {
    const current = store.get(groupedRequestsAtom)[key];
    if (current?.phase !== 'queued' || current.deadline !== deadline) return;
    if (!canSend()) {
      undoQueuedRequest(store, key);
      return;
    }
    setGroupedRequest(store, key, { phase: 'sending' });
    void send().then(
      (request) => setGroupedRequest(store, key, { phase: 'live', request }),
      (error: unknown) =>
        setGroupedRequest(store, key, {
          phase: 'error',
          error: error instanceof Error ? error : new Error('Request failed'),
        }),
    );
  }, UNDO_DURATION_MS);
}
